import React, { useMemo, useState } from 'react';
import {
  Alert,
  Drawer,
  Input,
  Select,
  Text,
  useToast,
} from 'barte-design-system';
import {
  Bolsao,
  COMPANIES,
} from './bolsao-mock-data';
import './Bolsao.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  bolsoes: Bolsao[];
  onCreate: (novo: Bolsao) => void;
}

/**
 * Drawer de criação de bolsão — 3 campos: Company, % sobre TPV e Teto (opcional).
 * Regra de 1 bolsão por company: todas as companies aparecem no Select,
 * mas ao tentar salvar uma que já possui bolsão o sistema exibe o aviso e bloqueia.
 */
export function DrawerNovoBolsao({ isOpen, onClose, bolsoes, onCreate }: Props) {
  const toast = useToast();

  const [companyId, setCompanyId] = useState('');
  const [percent, setPercent] = useState('');
  const [teto, setTeto] = useState('');
  const [jaTemBolsao, setJaTemBolsao] = useState(false);

  const company = useMemo(
    () => COMPANIES.find((c) => c.id === companyId) ?? null,
    [companyId],
  );

  function reset() {
    setCompanyId('');
    setPercent('');
    setTeto('');
    setJaTemBolsao(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function parseNumber(value: string): number | null {
    const n = Number(value.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) && value.trim() !== '' ? n : null;
  }

  function handleSave() {
    if (!company) return;

    // Regra: apenas 1 bolsão por company
    if (bolsoes.some((b) => b.payfacId === company.id)) {
      setJaTemBolsao(true);
      return;
    }

    const percentValue = parseNumber(percent);
    if (percentValue === null) return;
    const tetoValue = parseNumber(teto);

    onCreate({
      id: `BOL-${String(Math.floor(1000 + bolsoes.length)).padStart(4, '0')}`,
      payfac: company.nome,
      payfacId: company.id,
      percentTpv: percentValue,
      teto: tetoValue,
      esperado: null, // recém-criado: ainda sem TPV acumulado → "Calculando..."
      adquirido: null,
      configuradoEm: '26/08/2026',
      ultimaAlteracao: { usuario: 'Você', data: '26/08/2026' },
    });

    toast.success({
      title: 'Bolsão criado',
      description: `O bolsão de ${company.nome} foi configurado. O cálculo do esperado começa com o próximo TPV.`,
    });
    handleClose();
  }

  const canSave = companyId !== '' && percent.trim() !== '';

  return (
    <Drawer
      isOpen={isOpen}
      title="Novo bolsão"
      onClose={handleClose}
      className="bolsao-drawer-novo"
      footer={{
        primaryButton: {
          children: 'Salvar',
          onClick: handleSave,
          disabled: !canSave,
        },
        secondaryButton: {
          children: 'Cancelar',
          variant: 'outline',
          onClick: handleClose,
        },
      }}
    >
      <div className="bolsao-form">
        {jaTemBolsao && company && (
          <Alert
            variant="warning"
            title="Esta company já possui um bolsão"
            description={`Cada company pode ter apenas um bolsão configurado. Para ajustar o de ${company.nome}, edite a configuração existente no detalhe.`}
            onClose={() => setJaTemBolsao(false)}
          />
        )}

        <Select
          label="Company"
          placeholder="Selecione a company"
          searchable
          options={COMPANIES.map((c) => ({
            value: c.id,
            label: `${c.nome} · ${c.id}`,
          }))}
          value={companyId}
          onChange={(value) => {
            setCompanyId(value);
            setJaTemBolsao(false);
          }}
        />

        <div className="bolsao-form-row">
          <Input
            label="% sobre TPV"
            placeholder="Ex.: 3"
            value={percent}
            onChange={(e) => setPercent((e.target as HTMLInputElement).value)}
          />
          <Input
            label="Teto"
            placeholder="R$ 0,00"
            helpText="Deixe em branco para não definir teto."
            value={teto}
            onChange={(e) => setTeto((e.target as HTMLInputElement).value)}
          />
        </div>

        <Text variant="body-xs" className="bolsao-form-hint">
          O bolsão esperado é calculado como a % configurada sobre o TPV da company,
          limitado ao teto (quando definido).
        </Text>
      </div>
    </Drawer>
  );
}
